import { useState } from "react";
import type { ConversationSummary } from "../../api";
import { getUserEmail, isAuthSessionActive, triggerSignOut } from "../../auth";
import { config, isOidcConfigured } from "../../config";
import { ConversationList } from "./ConversationList";
import { MemoryEditor } from "./MemoryEditor";

const MEMORY_OPEN_KEY = "bedrock-chat:memory-open";

type Props = {
  conversations: ConversationSummary[];
  conversationsLoading: boolean;
  activeConversationId: string | null;
  onSelectConversation: (id: string | null) => void;
  onNewConversation: () => void;
  onDeleteConversation: (id: string) => void;
  open: boolean;
  onClose: () => void;
};

export function Sidebar({
  conversations,
  conversationsLoading,
  activeConversationId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  open,
  onClose,
}: Props) {
  const [memoryOpen, setMemoryOpen] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(MEMORY_OPEN_KEY) !== "0";
    } catch {
      return true;
    }
  });
  const toggleMemory = () => {
    setMemoryOpen((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(MEMORY_OPEN_KEY, next ? "1" : "0");
      } catch {
        // Storage write failures are non-fatal — preference just won't persist.
      }
      return next;
    });
  };
  return (
    <>
      {open && (
        <button
          type="button"
          aria-label="Close sidebar"
          className="sidebar-backdrop no-print"
          onClick={onClose}
        />
      )}
      <aside className={`sidebar no-print ${open ? "sidebar-open" : ""}`}>
        <div className="sidebar-header">
          <h2>{config.appName}</h2>
        </div>

        <div className="sidebar-section">
          <button
            type="button"
            className="sidebar-button sidebar-button--primary"
            onClick={onNewConversation}
          >
            <span>+</span>
            <span>New chat</span>
          </button>
        </div>

        <div className="sidebar-scroll">
          <div className="sidebar-section" style={{ flex: 1, minHeight: 0 }}>
            <div className="sidebar-section-header">
              <h3>Conversations</h3>
            </div>
            <ConversationList
              conversations={conversations}
              loading={conversationsLoading}
              activeId={activeConversationId}
              onSelect={onSelectConversation}
              onDelete={onDeleteConversation}
            />
          </div>

          <div className={`sidebar-section ${memoryOpen ? "" : "sidebar-section--collapsed"}`}>
            <button
              type="button"
              className="sidebar-section-header sidebar-section-toggle"
              onClick={toggleMemory}
              aria-expanded={memoryOpen}
            >
              <h3>Memory</h3>
              <span className="sidebar-section-chevron" aria-hidden="true">
                {memoryOpen ? "▾" : "▸"}
              </span>
            </button>
            {memoryOpen && <MemoryEditor />}
          </div>
        </div>

        <SidebarFooter />
      </aside>
    </>
  );
}

function SidebarFooter() {
  const showAuth = isOidcConfigured() && isAuthSessionActive();
  const email = getUserEmail() ?? config.devUser ?? "local dev";
  return (
    <div className="sidebar-footer">
      <span className="sidebar-user">{email}</span>
      <span className="sidebar-meta">{config.env}</span>
      {showAuth && (
        <button type="button" onClick={triggerSignOut}>
          Sign out
        </button>
      )}
    </div>
  );
}
